import React, { useState } from "react";
import { RCCButton, RCCGrid, RCCIcon, RCCInput, RCCTooltip } from "react-common-components";
import CheckPermission from "src/components/CheckPermission";
import InputNumber from "src/components/InputNumber";
import Title from "src/components/Title";
import { useAuth } from "src/context/Auth/context";
import { PermissionEnum } from "src/context/Auth/interface";
import { useTranslation } from "src/context/Translations/context";
import { IndicatorFieldType } from "src/pages/Indicators/Fields/interface";
import formatLink from "src/utils/formatLink";
import { IComponentValuesIndicator } from "./interface";
import Container from "./styles";

const getIcon = (formulaType: IndicatorFieldType | null) => {
	if(formulaType === IndicatorFieldType.FORMULA)
		return "xSquare";
	if(formulaType === IndicatorFieldType.INTEGRATION)
		return "database";
	// if(formulaType === 4)
	return "cloud";
};

const ValuesIndicator: React.FC<IComponentValuesIndicator> = ({
	id,
	label,
	value,
	index,
	comment,
	register,
	control,
	link,
	fieldId,
	isGoal,
	isCompareGoal,
	formula,
	onChange,
	openChangeField,
	openIntegrationLogs,
	formulaType,
	decimalPlaces,
	permission
}) => {
	const { translate } = useTranslation();

	const { superUser, administrator } = useAuth();

	const [showLink, setShowLink] = useState<boolean>(false);
	const [showComment, setShowComment] = useState<boolean>(!(isGoal || isCompareGoal));
	const [hasComment, setHasComment] = useState(comment.trim().length > 0);
	const [linkButtonText, setLinkButtonText] = useState<string | undefined>(formatLink(link));
	const onBlurCaptureLink = (val: string) => {
		if(val.trim().length === 0){
			setLinkButtonText(undefined);
			return;
		}
		if(!val.includes("http://") && !val.includes("https://")){
			setLinkButtonText(`https://${val.trim()}`);
			return;
		}
		setLinkButtonText(val.trim());
	};
	const onBlurCaptureComment = (val: string) => {
		setHasComment(val.trim().length > 0);
	};
	const [executeOnBlur, setExecuteOnBlur] = useState(false); 


	const disabledEditGoal = !((isGoal && permission >= 4) || !isGoal || superUser || administrator);

	return (
		<Container displayContainer={isGoal || isCompareGoal || false}>
			{(isGoal || isCompareGoal) && (
				<div className="title">
					{isGoal ? translate("Meta") : translate("Comparar com meta")}
					<RCCIcon 
						name={isGoal ? "target" : "thumbs"}
						color="var(--purple-lighter)"
						size={16}
						style={{ opacity: "0.75" }}
					/>
				</div>
			)}
			<div className="body">
				<RCCGrid container item justifyContent="space-between" alignItems="baseline" style={{marginBottom: 0, paddingBottom: 0}}>
					<Title
						color="purple"
						size="xs"
						marginBottom={0}
					>					
						{label}
					</Title>
					<div style={{display: "flex", alignItems: "center"}}>
						{linkButtonText && (
							<RCCTooltip
								text="Link"
								color="purple"
								position="leftEnd"
							>
								<RCCButton
									layout="isRounded"
									icon={{
										name: "linkExternal"
									}}
									color="purple"
									onClick={() => window.open(linkButtonText, "_blank")}
								/>
							</RCCTooltip>
						)}
						<RCCTooltip
							text="Link"
							color={showLink ? "blue" : "grey"}
							position="leftEnd"
						>
							<RCCButton
								layout="isRounded"
								icon={{
									name: "link"
								}}
								color={showLink ? "blue" : "grey"}
								onClick={() => setShowLink((prevState) => !prevState)}
							/>
						</RCCTooltip>
						{(isGoal || isCompareGoal) && (
							<RCCTooltip
								text={translate("Comentário")}
								color={showComment || hasComment ? "blue" : "grey"}
								position="leftEnd"
							>
								<RCCButton
									layout="isRounded"
									icon={{
										name: "comment"
									}}
									color={showComment || hasComment ? "blue" : "grey"}
									onClick={() => setShowComment((prevState) => !prevState)}
								/>
							</RCCTooltip>
						)}
						{(formulaType === IndicatorFieldType.INTEGRATION) &&(
							<RCCTooltip
								color="grey"
								text={translate("Visualizar Logs de Integração")}
								position="leftEnd"	
							>
								<RCCButton
									color="grey"
									icon={{
										name: "stack"
									}}
									layout="isRounded"
									onClick={openIntegrationLogs}
								/>
							</RCCTooltip>	
						)}
						{!disabledEditGoal && (
							<CheckPermission minimumPermissionRequired={PermissionEnum.CREATE_UPDATE} path="/indicators/fields">
								<RCCTooltip
									text={translate("Alterar campo")}
									color="grey"
									position="leftEnd"
								>
									<RCCButton
										layout="isRounded"
										icon={{
											name: "pencil"
										}}
										color="grey"
										onClick={() => openChangeField()}
									/>
								</RCCTooltip>
							</CheckPermission>
						)}			
					</div>
				</RCCGrid>
				<RCCGrid container item spacing={1}>
					<input 
						type="text"
						value={String(id)}
						{...register(`val[${index}].iqd001Id`)} 
						style={{display: "none"}}
						readOnly
					/>
					<input 
						type="text"
						value={String(fieldId)}
						{...register(`val[${index}].iqb001Id`)} 
						style={{display: "none"}}
						readOnly
					/>
					<RCCGrid
						container 
						item
						xs={12}
						sm={isGoal || isCompareGoal ? 12 : 4}
						style={{position: "relative"}}
					>
						<InputNumber 
							control={control}
							label={" "}
							name={`val[${index}].iqd001Valor`}
							defaultValue={id === 0 ? undefined : value}
							maxLength={17}
							styleInline={`height: 34px; margin: 0 0 5px 0; ${isGoal || isCompareGoal ? ">input { font-size: 1rem !important; }" : ""}`}
							onChange={() => setExecuteOnBlur(true)}
							onBlur={() => {
								if(!executeOnBlur)
									return;
								onChange();
							}}
							decimalScale={decimalPlaces}
							disabled={(formula !== null && formula !== "") || disabledEditGoal}
						/>
						{formula !== null && formula !== "" && formulaType !== null && formulaType !== IndicatorFieldType.INFORMED && (
							<RCCTooltip
								color="grey"
								text={formula}
								styleInline="width: max-content; max-width: 250px;"
							>
								<RCCIcon 
									name={getIcon(formulaType)}
									color="var(--input-font-color)"
									size={24}
									style={{
										position: "absolute",
										top: "0.5rem",
										right: "1rem"
									}}
								/>
							</RCCTooltip>
						)}
					</RCCGrid>
					<RCCGrid
						container
						item
						xs={12}
						sm={isGoal || isCompareGoal ? 12 : 8}
						style={showComment ? {} : { display: "none" }}
					>
						<RCCInput 
							label={translate("Comentário")}
							name={`val[${index}].iqd001Comentario`}
							defaultValue={comment}
							register={register}
							onChangeCapture={onChange}
							onBlurCapture={(e) => onBlurCaptureComment(e.target.value)}
							disabled={disabledEditGoal}
							styleInline={`height: 34px; margin: 0 0 5px 0;
							>input.input-of-component-input:focus + label.label-of-component-input, label.label-of-component-input.isFilled-true, >input.input-of-component-input:-webkit-autofill + label.label-of-component-input{
								top: -16px;
							}
						`}
							maxLength={400}
						/>
					</RCCGrid>
				</RCCGrid>

				<RCCGrid 
					container 
					item
					style={showLink ? {} : { display: "none" }}
				>
					<RCCInput 
						name={`val[${index}].iqd001Linkexterno`}
						label="Link"
						register={register}
						defaultValue={link}
						onChangeCapture={onChange}
						styleInline="margin-top: -10px;"
						onBlurCapture={(e) => onBlurCaptureLink(e.target.value)}
						maxLength={1000}
						disabled={disabledEditGoal}
					/>
				</RCCGrid>
			</div>
		</Container>
	);
};

export default ValuesIndicator;
